import React from 'react';
import styles from './FeaturesPage.module.css';

const featureBlocks = [
  {
    id: '01',
    title: 'AI-Powered Weekly Strategy',
    desc:
      'LeanWeek-AI turns your profile, equipment, and weekly context into a practical roadmap you can actually follow. The experience is structured to reduce friction, not create more decisions.',
    points: [
      'Eliminate decision fatigue',
      'Equipment-aware weekly planning',
      'Structured fat-loss pacing',
    ],
    image: '/screenGenerating.png',
    alt: 'LeanWeek AI plan generation screen',
    badge: 'Generating Engine',
  },
  {
    id: '02',
    title: 'Privacy-First Local Experience',
    desc:
      'Your weekly fitness planning experience is designed around privacy. LeanWeek keeps the workflow lightweight, local-first, and free from account-heavy friction or invasive tracking patterns.',
    points: [
      'No mandatory account setup',
      'No invasive analytics layer',
      'Local-first product model',
    ],
    image: '/screenDashboard.png',
    alt: 'LeanWeek dashboard screen',
    badge: 'Private by Design',
    reverse: true,
  },
  {
    id: '03',
    title: 'Rich Daily Execution',
    desc:
      'Every plan becomes actionable inside a guided workout view with durations, step targets, burn estimates, and structured exercise cards that make execution feel immediate.',
    points: [
      'Clear sets, reps, and rest',
      'Practical daily targets',
      'Action-ready workout screens',
    ],
    image: '/screenMondayWorkout.png',
    alt: 'LeanWeek Monday workout screen',
    badge: 'Workout Protocol',
  },
  {
    id: '04',
    title: 'Social-Ready Progress Sharing',
    desc:
      'LeanWeek creates polished share outputs that make your progress feel tangible. Morning previews and recap cards are designed to be saved, shared, and revisited.',
    points: [
      'Morning share cards',
      'Evening recap visuals',
      'Premium social-ready presentation',
    ],
    image: '/screenMorningPreview.png',
    alt: 'LeanWeek morning preview social card',
    badge: 'Share Exports',
    reverse: true,
  },
  {
    id: '05',
    title: 'Weekly Roadmap Clarity',
    desc:
      'See the week at a glance with a roadmap that makes upcoming training, recovery, steps, and workout time easy to understand without clutter.',
    points: [
      'Readable weekly structure',
      'Built for adherence',
      'Export-friendly weekly view',
    ],
    image: '/screenWeeklyPlan.png',
    alt: 'LeanWeek weekly roadmap screen',
    badge: 'Weekly Plan',
  },
];

const PhoneMockup = ({ src, alt }) => {
  return (
    <div className={styles.phoneWrap}>
      <div className={styles.deviceGlow} />
      <div className={styles.iphoneMockup}>
        <div className={styles.iphoneInner}>
          <div className={styles.dynamicIsland} />
          <img src={src} alt={alt} className={styles.iphoneScreen} />
        </div>

        <div className={styles.sideButtonLeftTop} />
        <div className={styles.sideButtonLeftBottom} />
        <div className={styles.sideButtonRight} />
      </div>
    </div>
  );
};

const FeaturesPage = () => {
  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroGrid}>
            <div className={styles.heroCopy}>
              <span className={styles.eyebrow}>Product Deep Dive</span>

              <h1 className={styles.heroTitle}>
                Everything you need for a <span>Leaner Week.</span>
              </h1>

              <p className={styles.heroText}>
                LeanWeek-AI distills weekly fitness planning into a private,
                premium, action-oriented system built for real consistency.
              </p>

              <div className={styles.heroStats}>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Workflow</span>
                  <strong>Plan → Execute → Share</strong>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Experience</span>
                  <strong>Private, Local, Practical</strong>
                </div>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <PhoneMockup
                src="/screen.png"
                alt="LeanWeek main app experience"
              />
            </div>
          </div>
        </div>
      </header>

      <section className={styles.featureGrid}>
        <div className={styles.container}>
          {featureBlocks.map((block) => (
            <article
              key={block.id}
              className={`${styles.row} ${block.reverse ? styles.reverse : ''}`}
            >
              <div className={styles.textSide}>
                <div className={styles.blockTop}>
                  <span className={styles.number}>{block.id}</span>
                  <span className={styles.blockBadge}>{block.badge}</span>
                </div>

                <h2 className={styles.blockTitle}>{block.title}</h2>
                <p className={styles.blockDesc}>{block.desc}</p>

                <ul className={styles.benefitList}>
                  {block.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.visualSide}>
                <div className={styles.visualCard}>
                  <PhoneMockup src={block.image} alt={block.alt} />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.safetySection}>
        <div className={styles.container}>
          <div className={styles.safetyBox}>
            <span className={styles.safetyKicker}>Sustainable Framing</span>
            <h3>A note on realistic progress.</h3>
            <p>
              LeanWeek-AI is built for general fitness guidance and habit
              consistency. It is not a medical product, and it should not
              replace professional medical or clinical advice.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;